import { useMemo } from 'react';
import { usePortfolios } from '@/hooks/portfolio/usePortfolio';
import { useCompanyReportTranslation } from '@/pages/CompanyReport/hooks/useTranslation';
import TickIcon from '@simplywallst/ui-core/icons/TickSolid';
import PlusIcon from '@simplywallst/ui-core/icons/PlusSolid';
import { MODAL } from '../constants';
import { cnLite } from '../utils';
import type { ActiveModal, Action, TrackEvent } from '../types';
import { useUser } from '@/hooks/useUser';

export const usePortfolioAction = ({
  companyId,
  tickerSymbol,
}: {
  companyId: string | null;
  tickerSymbol: string | null;
}): Action => {
  const t = useCompanyReportTranslation();
  const { data: user } = useUser();
  const { isFetched: portfoliosIsFetched, data: portfolios = [] } =
    usePortfolios({ enabled: user?.loggedIn });

  // The companyId has mismatched case between the two APIs
  const uppercaseCompanyId = companyId?.toUpperCase();
  const isInPortfolio = useMemo(
    () =>
      portfoliosIsFetched && uppercaseCompanyId
        ? portfolios.some((p) =>
            p.holdings.some((h) => h.companyId === uppercaseCompanyId)
          )
        : false,
    [portfoliosIsFetched, portfolios, uppercaseCompanyId]
  );

  const handleClick = ({
    isUserLoggedIn,
    trackEvent,
    setActiveModal,
  }: {
    isUserLoggedIn: boolean;
    trackEvent: TrackEvent;
    setActiveModal: (modal: ActiveModal) => void;
  }) => {
    trackEvent({
      type: 'track',
      subject: 'addToPortfolio',
      ...(isInPortfolio && { modifier: 'active' }),
      action: 'click',
    });
    if (!isUserLoggedIn) {
      trackEvent({
        type: 'track',
        subject: 'guestRegistrationModal',
        modifier: 'addToPortfolio',
        action: 'click',
      });
      setActiveModal(MODAL.REGISTRATION);
      return;
    }
    setActiveModal(MODAL.ADD_REMOVE_COMPANY);
  };

  /**
   * Avoid layout shift when the user is "holding" this stock after page load.
   */
  const largeLabel = useMemo(
    () => (
      <span className="grid place-items-center [&>*]:[grid-area:1/1]">
        <span
          className={cnLite(
            'flex items-center gap-x0_5',
            isInPortfolio && 'invisible'
          )}
        >
          <PlusIcon className="-mt-px shrink-0" />
          <span className="inline-block @[340px]:hidden">
            {t('header.actions.portfolio')}
          </span>
          <span className="hidden @[340px]:inline-block">
            {t('header.actions.addToPortfolio')}
          </span>
        </span>
        <span className={cnLite('flex', !isInPortfolio && 'invisible')}>
          <TickIcon className="-mt-px" />

          {t('header.portfolio.holding')}
        </span>
      </span>
    ),
    [isInPortfolio, t]
  );

  return {
    key: 'portfolio',
    cyId: 'add-to-portfolio',
    isDisabled: !tickerSymbol || !companyId,
    largeProps: {
      className: isInPortfolio
        ? 'transition-[colors,opacity] duration-500'
        : undefined,
      label: largeLabel,
      variant: 'primary',
    },
    handleClick,
  };
};
